import {useEffect, useMemo, useCallback, useRef} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useMapStore} from '../../map/store/MapStore';
import {useMarketerSelectionStore} from '../store/MarketerSelectionStore';
import {GetMarketersInBoundsUseCase} from '../useCases/GetMarketersInBoundsUseCase';
import {MapboxMapService} from '../../../Services/map/MapService';
import {Marketer} from '../models/marketer.model';
import {useAuth} from '../../auth/hooks/useAuth';

/**
 * Hook for fetching marketers data for map display
 * @param shouldFetch - Whether to enable data fetching (default: true)
 * @returns Object containing marketers data, loading state, and error
 */
export const useMarketersMap = (shouldFetch: boolean = true) => {
  // الحصول على الخدمات والـ use cases
  const mapService = MapboxMapService.getInstance();
  const getMarketersInBounds = new GetMarketersInBoundsUseCase(mapService);

  // استخدام الـ stores المنفصلة
  const {bounds, error: mapError} = useMapStore();
  const {marketers, setMarketers, setLoading, setError} = useMarketerSelectionStore();
  const {isAuthenticated, initialized} = useAuth();

  const debounceRef = useRef<number>();
  const lastBoundsRef = useRef<string>('');

  // دالة debounce لتأخير جلب البيانات
  const debouncedFetch = useCallback((_queryKey: any[], queryFn: () => Promise<any>) => {
    return new Promise((resolve, reject) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(async () => {
        try {
          // Add additional delay to ensure authentication is ready
          await new Promise(resolve => setTimeout(resolve, 500));
          const result = await queryFn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, 800);
    });
  }, []);

  // تحسين query key مع debounce
  const queryKey = useMemo(() => {
    const key = [
      'nearbyMarketers',
      Math.round(bounds.sw.lat * 100) / 100,
      Math.round(bounds.sw.lng * 100) / 100,
      Math.round(bounds.ne.lat * 100) / 100,
      Math.round(bounds.ne.lng * 100) / 100
    ];

    const keyString = JSON.stringify(key);
    const boundsChanged = keyString !== lastBoundsRef.current;
    if (boundsChanged) {
      lastBoundsRef.current = keyString;
    }

    return key;
  }, [bounds.sw.lat, bounds.sw.lng, bounds.ne.lat, bounds.ne.lng]);

  const {
    data: marketersData,
    isLoading,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey,
    queryFn: () => debouncedFetch(queryKey, () => getMarketersInBounds.execute(
      bounds,
      true, // includeDetails
      'market_developer', // role
      true, // onlyActive
      1000 // limit
    )),
    enabled: shouldFetch && initialized && isAuthenticated, // Only enable when shouldFetch is true and user is authenticated
    placeholderData: (previousData) => previousData,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
    retryDelay: 500,
    // Add a small delay to ensure authentication state is properly set
    refetchInterval: false
  }) as { data: Marketer[], isLoading: boolean, error: any, refetch: any, isFetching: boolean };

  // تحديث حالة التحميل
  useEffect(() => {
    const shouldShowLoading = isLoading && !marketersData && marketers.length === 0;
    setLoading(shouldShowLoading);
  }, [isLoading, marketersData, marketers.length, setLoading]);

  // تحديث حالة الخطأ
  useEffect(() => {
    if (error) {
      const errorMessage = error instanceof Error ? error.message : 'حدث خطأ في جلب المسوقين';
      setError(errorMessage);
    } else {
      setError(undefined);
    }
  }, [error, setError]);

  // تحديث المسوقين
  useEffect(() => {
    if (marketersData) {
      setMarketers(marketersData);
    }
  }, [marketersData, setMarketers]);


  // تنظيف
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    marketers: marketersData || [],
    isLoading: isLoading && !marketersData,
    isFetching,
    error: error || mapError,
    refetch
  };
};
